import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Wifi, Coffee, Flame, Car, Trees } from 'lucide-react';
import Navbar from '../components/Navbar';
import FloatingBookingBar from '../components/FloatingBookingBar';

const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  const reviewParallax = useTransform(scrollY, [0, 800], [0, -40]);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const amenities = [
    { icon: <Wifi size={32} />, name: 'High-Speed WiFi' },
    { icon: <Coffee size={32} />, name: 'All Meals Included' },
    { icon: <Flame size={32} />, name: 'Bonfire Experience' },
    { icon: <Car size={32} />, name: 'Free Parking' },
    { icon: <Trees size={32} />, name: 'Nature Trails' },
    { icon: <MapPin size={32} />, name: 'Scenic Location' },
  ];
  const reviews = [
  {
    name: 'Aarav Mehta',
    location: 'Delhi',
    text: 'Waking up to birds instead of alarms changed something in me. The silence here is unreal.',
  },
  {
    name: 'Sonal Kapoor',
    location: 'Lucknow',
    text: 'Best jungle stay in Pilibhit. The safari arrangements were seamless and ethical.',
  },
  {
    name: 'Rohan Singh',
    location: 'Bangalore',
    text: 'If peace had an address, this would be it. Special thanks to Zeeshan and team for the wonderful experience.',
  },
];
const [activeReview, setActiveReview] = useState(0);

useEffect(() => {
  const timer = setInterval(() => {
    setActiveReview((prev) => (prev + 1) % reviews.length);
  }, 6000);
  return () => clearInterval(timer);
}, []);



  return (
    <div className="min-h-screen" style={{ background: '#F9F8F6' }}>
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
        >
          <source
            src="https://res.cloudinary.com/dwwjvrlvg/video/upload/v1768658605/IMG_5922_zfsaeb.mp4"
            type="video/mp4"
          />
        </video>
        <div className="hero-overlay" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 text-center text-white px-4 max-w-4xl"
        >
          <p className="overline text-white/80 mb-4" data-testid="hero-tagline">YOUR ESCAPE INTO NATURE</p>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="hero-title">
            <span className="block">Welcome to</span>
            <span className="block">The Bagh Home</span>   
          </h1>
          <p className="text-xl md:text-2xl mb-12 font-light opacity-90" data-testid="hero-subtitle">
            Experience tranquility in the heart of nature
          </p>
          <Link to="/rooms">
            <button className="glass px-10 py-5 text-lg font-medium rounded-full hover:bg-white/30 transition-all duration-300 hover:scale-105 active:scale-95" data-testid="book-stay-button">
              Book Your Stay
            </button>
          </Link>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="section-padding" data-testid="about-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="md:col-span-7"
          >
            <p className="overline text-[#8F9E8B] mb-4">ABOUT US</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}>
              A Sanctuary Amidst Nature
            </h2>
            <p className="text-lg leading-relaxed mb-6" style={{ color: '#64748B' }}>
              The Bagh Home is more than just a place to stay—it's an experience. Nestled in the serene embrace of nature, 
              our homestay offers a perfect blend of comfort and tranquility. Each moment here is designed to help you 
              disconnect from the chaos and reconnect with what truly matters.
            </p>
            <p className="text-lg leading-relaxed" style={{ color: '#64748B' }}>
              Whether you're seeking adventure or peaceful solitude, The Bagh Home provides the perfect backdrop for 
              your escape. Wake up to birdsong, breathe in fresh mountain air, and let nature restore your soul.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="md:col-span-5"
          >
            <img
              src="https://res.cloudinary.com/dwwjvrlvg/image/upload/v1768660008/4c17c7a1-2351-4ad3-8025-464631828e31_oeleq4.jpg"
              alt="The Bagh Home View"
              className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="section-padding" style={{ background: 'white' }} data-testid="amenities-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="overline text-[#8F9E8B] mb-4">AMENITIES</p>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}>
              Everything You Need
            </h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {amenities.map((amenity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
                data-testid={`amenity-${index}`}
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: '#F9F8F6', color: '#1A3C34' }}>
                  {amenity.icon}
                </div>
                <p className="font-medium" style={{ color: '#1A202C' }}>{amenity.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden" data-testid="experience-section">
        <img
          src="https://images.unsplash.com/photo-1700900345238-428c7fb0155e?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Experience Nature"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="relative z-10 text-center text-white px-4"
        >
          <p className="text-3xl md:text-4xl font-light italic" style={{ fontFamily: 'Playfair Display, serif' }}>
            "Experience nature at its quietest."
          </p>
        </motion.div>
      </section>

      {/* Reviews Section */}
      <section className="section-padding" style={{ background: 'white' }}>
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="overline text-[#8F9E8B] mb-4">GUEST STORIES</p>
            <h2
              className="text-4xl md:text-5xl font-bold tracking-tight"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              Voices From The Forest
            </h2>
          </motion.div>
      
          <motion.div
            key={activeReview}
            style={{ y: reviewParallax }}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl p-10 shadow-xl max-w-3xl mx-auto backdrop-blur-[1px]"
          >
            <p
              className="text-lg leading-relaxed mb-8"
              style={{ color: '#64748B' }}
            >
              “{reviews[activeReview].text}”
            </p>
      
            <p
              className="text-lg font-semibold"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              {reviews[activeReview].name}
            </p>
      
            <p className="text-sm text-[#8F9E8B]">
              {reviews[activeReview].location}
            </p>
          </motion.div>
      
          <div className="flex justify-center gap-3 mt-8">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveReview(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === activeReview ? 'bg-[#1A3C34]' : 'bg-[#D1D5DB]'
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>


      {/* Location Section */}
      <section className="section-padding" data-testid="location-section">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <p className="overline text-[#8F9E8B] mb-4">LOCATION</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}>
              Find Us
            </h2>
            <p className="text-lg mb-12" style={{ color: '#64748B' }}>
              Nestled in the pristine jungle, easily accessible yet perfectly secluded.
            </p>
          </motion.div>
          <div className="rounded-3xl overflow-hidden shadow-2xl h-[400px] border-4 border-white">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3500.032694611413!2d80.06985267550284!3d28.688668575634125!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39a04b006c99ab77%3A0x25e9f1fb87dbf8a2!2sBagh%20home%20Pilibhit%20tiger%20reserve!5e0!3m2!1sen!2sin!4v1768657947345!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'sepia(20%) hue-rotate(60deg)' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="The Bagh Home Location"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16" style={{ background: '#1A3C34', color: 'white' }} data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>The BaghHome</h3>
          <p className="text-white/70 mb-6">Your Escape Into Nature</p>
          <div className="flex justify-center gap-8 mb-8">
            <a href="tel:+918218387038" className="hover:text-[#8F9E8B] transition-colors">+91 8218387038</a>
            <a href="mailto:info@baghhome.com" className="hover:text-[#8F9E8B] transition-colors">info@baghome.com</a>
          </div>
          <div className="flex justify-center gap-6">
            <a href="https://wa.me/918218387038" target="_blank" rel="noopener noreferrer" className="hover:text-[#8F9E8B] transition-colors">
              WhatsApp
            </a>
            <a href="https://www.instagram.com/baghhome_pilibhit/?hl=en" target="_blank" rel="noopener noreferrer" className="hover:text-[#8F9E8B] transition-colors">
              Instagram
            </a>
          </div>
          <p className="text-white/50 text-sm mt-8">&copy; 2025 The BaghHome. All rights reserved.</p>
        </div>
      </footer>

      <FloatingBookingBar />
    </div>
  );
};

export default Home;
